import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useDismiss,
  useRole,
  useInteractions,
  useClientPoint,
  arrow,
  safePolygon,
  useHover,
} from '@floating-ui/react';
import { HiddenToggletipState, ToggletipProps } from './types';
import { useId } from '@redsift/design-system';

export function useToggletip({
  color,
  defaultOpen,
  delay,
  isModal,
  isOpen: propsIsOpen,
  maxWidth,
  minWidth,
  offset: propsOffset,
  onOpen,
  placement,
  role: propsRole,
  toggletipId: propsToggletipId,
  triggerClassName,
  width,
  setHideInsteadOfClose,
  hideInsteadOfClose,
}: Omit<ToggletipProps, 'children'> & HiddenToggletipState) {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(propsIsOpen ?? defaultOpen);

  const [_id] = useId();
  const toggletipId = propsToggletipId ?? _id;

  useEffect(() => {
    setIsOpen(propsIsOpen ?? defaultOpen);
  }, [propsIsOpen, defaultOpen]);

  const handleOpen = useCallback(
    (collapsed: boolean) => {
      if (onOpen) {
        onOpen(collapsed);
      }
      if (propsIsOpen === undefined || propsIsOpen === null) {
        setIsOpen(collapsed);
      }
    },
    [onOpen]
  );

  const middleware = [
    offset(propsOffset ?? 8),
    flip({
      fallbackAxisSideDirection: 'start',
    }),
    shift({ padding: 8 }),
    arrow({
      element: arrowRef,
    }),
  ];

  middleware.push(
    size({
      apply({ availableHeight, availableWidth, rects, elements }) {
        Object.assign(elements.floating.style, {
          maxHeight: `${availableHeight}px`,
          ...(minWidth !== undefined && {
            minWidth: `${
              minWidth === 'available-width'
                ? availableWidth
                : minWidth === 'trigger-width'
                ? rects.reference.width
                : minWidth
            }px`,
          }),
          ...(width !== undefined && {
            width: `${
              width === 'available-width' ? availableWidth : width === 'trigger-width' ? rects.reference.width : width
            }px`,
          }),
          ...(maxWidth !== undefined && {
            maxWidth: `${
              maxWidth === 'available-width'
                ? availableWidth
                : maxWidth === 'trigger-width'
                ? rects.reference.width
                : maxWidth
            }px`,
          }),
        });
      },
    })
  );

  const data = useFloating({
    placement: placement === 'client-point' ? 'left' : placement,
    open: isOpen,
    onOpenChange: handleOpen,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const context = data.context;
  const clientPoint = useClientPoint(context, { enabled: placement === 'client-point' });

  const hover = useHover(context, {
    move: false,
    delay: {
      open: delay,
      close: 0,
    },
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: propsRole });

  const interactions = useInteractions([hover, dismiss, role, clientPoint]);

  return React.useMemo(
    () => ({
      color,
      isOpen,
      handleOpen,
      ...interactions,
      ...data,
      arrowRef,
      isModal,
      triggerClassName,
      setHideInsteadOfClose,
      hideInsteadOfClose,
      toggletipId,
    }),
    [
      color,
      isOpen,
      handleOpen,
      interactions,
      data,
      arrowRef,
      isModal,
      triggerClassName,
      setHideInsteadOfClose,
      hideInsteadOfClose,
      toggletipId,
    ]
  );
}
